import { Mic, Square } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

type UseAudioRecorderOptions = {
  onRecordingComplete: (file: File) => void;
};

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export const useAudioRecorder = ({ onRecordingComplete }: UseAudioRecorderOptions) => {
  const { t } = useTranslation(['inbox_v2']);
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const cleanup = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => cleanup, [cleanup]);

  useEffect(() => {
    if (!isRecording) return;
    setDuration(0);
    const interval = setInterval(() => {
      setDuration((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isRecording]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      let mimeType: string;
      if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else {
        toast.error(t('conversation.audio_not_supported'));
        cleanup();
        return;
      }

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        let blobType: string;
        let ext: string;
        if (mimeType.startsWith('audio/ogg')) {
          blobType = 'audio/ogg';
          ext = 'ogg';
        } else if (mimeType.startsWith('audio/webm')) {
          blobType = 'audio/webm';
          ext = 'webm';
        } else {
          blobType = 'audio/mp4';
          ext = 'm4a';
        }
        const blob = new Blob(chunksRef.current, { type: blobType });
        const file = new File([blob], `voice-message.${ext}`, { type: blobType });
        onRecordingComplete(file);
        cleanup();
      };

      recorder.start();
      setIsRecording(true);
    } catch {
      toast.error(t('conversation.audio_permission_denied'));
      cleanup();
    }
  }, [onRecordingComplete, t, cleanup]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  return { isRecording, duration, startRecording, stopRecording };
};

type AudioRecorderButtonProps = {
  disabled: boolean;
  isRecording: boolean;
  onStart: () => void;
  onStop: () => void;
};

export const AudioRecorderButton = ({
  disabled,
  isRecording,
  onStart,
  onStop,
}: AudioRecorderButtonProps) => {
  const { t } = useTranslation(['inbox_v2']);

  if (isRecording) {
    return (
      <Tooltip key="stop">
        <TooltipTrigger asChild>
          <IconButton
            variant="dark"
            size="sm"
            type="button"
            onClick={onStop}
            className="[&_svg]:size-3 !opacity-100"
            data-testid="audio-recorder-stop"
          >
            <Square className="fill-white text-white" />
          </IconButton>
        </TooltipTrigger>
        <TooltipContent side="top">{t('conversation.audio_stop_tooltip')}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip key="record">
      <TooltipTrigger asChild>
        <IconButton
          variant="primary_dark_light"
          size="sm"
          type="button"
          disabled={disabled}
          onClick={onStart}
          data-testid="audio-recorder-button"
        >
          <Mic className="h-4 w-4" />
        </IconButton>
      </TooltipTrigger>
      <TooltipContent side="top">{t('conversation.audio_record_tooltip')}</TooltipContent>
    </Tooltip>
  );
};
